import actions from "../../actions";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useNonVideoEventMutation } from "./useNonVideoEventMutation";
import {
  addMinutesToTime,
  getDateString,
  getISODateTime,
  timeWithTimezoneToString,
} from "../../utils/dateTimeUtils";
import { getKeyCancellationReason } from "../../constants/cancelReasons";
import * as selectors from "selectors";
import { useEffect } from "react";

export default function useEventFormSubmit({
  selectedClinician,
  rescheduleAllInstances,
  secondaryClinicianUserId,
  cancellationReasons,
  recurringCancellationReasons,
}) {
  const dispatch = useDispatch();
  const { search } = useLocation();
  const cancelVideoCallSuccess = useSelector(selectors.getCancelVideoCallSuccess);
  const cancelVideoCallLoading = useSelector(selectors.getCancelVideoCallLoading);
  const cancelVideoCallFail = useSelector(selectors.getCancelVideoCallFail);

  const query = new URLSearchParams(search);
  const videoId = query.get("id");
  const isEditing = !!videoId;

  const { scheduleNonVideoEvent, rescheduleNonVideoEvent, cancelNonVideoEvent } =
    useNonVideoEventMutation();

  // Reset cancel video call state on unmount
  useEffect(() => {
    return () => dispatch(actions.resetCancelVideoCallState());
  }, []);

  const onVideoCallSubmit = (values, eventInfo) => {
    const { isRepeating, repeatEndType, repeatUntilDate, repeatDays, timeUnit } = values;
    const eventDate = getDateString(values.eventDate);
    const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezone = isEditing ? eventInfo.client_timezone : values.selectedTeamMember.timezone;
    const originalLocalTime = timeWithTimezoneToString(
      values.startTime,
      timezone,
      values.eventDate,
      localTimezone,
      "HH:mm"
    );
    const startDate = getISODateTime(eventDate, originalLocalTime, timezone);
    const endDate = getISODateTime(
      eventDate,
      addMinutesToTime(originalLocalTime, values.duration),
      timezone
    );

    const sharedPayload = {
      startDate,
      endDate,
      clinicianUserId: selectedClinician.clinician_user_id,
      secondaryClinicianUserId,
      // billingType passes through the raw select value verbatim, so new enum
      // values (e.g. "IN_PERSON_ASSESSMENT" from the Off Platform Session flow)
      // are persisted without any client-side transformation.
      billingType: values.serviceType,
      offPlatform: values.event === "doxy-session",
      repeatDays,
      overrideSchedule: true,
      repeatEndType,
      repeatUntilDate,
      timeUnit,
      timeInterval: values.timeInterval,
      isRepeating,
      memberId: values.selectedTeamMember.userId,
      isTentative: values.isTentative,
      timezone,
      originalLocalTime,
    };

    if (isEditing) {
      const resched = {
        ...sharedPayload,
        videoId,
        rescheduleAllInstances: rescheduleAllInstances === "false" ? false : true,
      };

      dispatch(actions.rescheduleVideoCall(resched));
    } else {
      const sched = {
        ...sharedPayload,
        clientId: values?.selectedClient?.id,
        bcbaEmail: selectedClinician.email,
      };

      dispatch(actions.scheduleVideoCall(sched));
    }
  };
  const onOtherServicesSubmit = (values) => {
    const { isRepeating, serviceType, repeatEndType, repeatUntilDate, repeatDays, selectedClient } =
      values;
    const { id: clientId } = selectedClient;
    const eventDate = getDateString(values.eventDate);
    const timezone = values.selectedTeamMember.timezone;
    const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const originalLocalTime = timeWithTimezoneToString(
      values.startTime,
      timezone,
      values.eventDate,
      localTimezone,
      "HH:mm"
    );
    const startDate = getISODateTime(eventDate, originalLocalTime, timezone);
    const endDate = getISODateTime(
      eventDate,
      addMinutesToTime(originalLocalTime, values.duration),
      timezone
    );

    const sharedPayload = {
      startDate,
      endDate,
      clinicianUserId: selectedClinician.user_id,
      repeatDays,
      repeatEndType,
      repeatUntilDate,
      timeUnit: values.timeUnit,
      timeInterval: values.timeInterval,
      isRepeating,
      memberId: values.selectedTeamMember.userId,
      isTentative: values.isTentative,
      serviceType,
      timezone,
      override: true,
      originalLocalTime,
    };

    if (isEditing) {
      rescheduleNonVideoEvent.mutate({
        ...sharedPayload,
        rescheduleAllInstances: rescheduleAllInstances === "false" ? false : true,
        id: videoId,
      });
    } else {
      scheduleNonVideoEvent.mutate({ ...sharedPayload, clientId });
    }
  };

  const onCancelSubmit = ({
    isNonVideoEvent,
    eventInfo,
    cancelAllInstances,
    message,
    responsibleForCancellation,
    cancellationReasonId,
  }) => {
    const activeCancellationReasons = cancelAllInstances
      ? recurringCancellationReasons
      : cancellationReasons;
    const selectedCatalogReason = (activeCancellationReasons || []).find(
      (reason) => String(reason.id) === String(cancellationReasonId)
    );
    const selectedCatalogReasonId = selectedCatalogReason?.id;
    const cancellationPayload = {
      message,
      responsibleForCancellation: isNonVideoEvent || selectedCatalogReasonId !== undefined
        ? responsibleForCancellation
        : getKeyCancellationReason(responsibleForCancellation),
      ...(selectedCatalogReasonId !== undefined
        ? { cancellationReasonId: selectedCatalogReasonId }
        : {}),
    };
    if (isNonVideoEvent) {
      cancelNonVideoEvent.mutate({
        id: eventInfo.id,
        cancelAllInstances,
        ...cancellationPayload,
      });
    } else {
      dispatch(
        actions.cancelVideoCall({
          ...cancellationPayload,
          id: eventInfo.id,
          clinicianUserId: eventInfo.clinician_user_id,
          cancelAllInstances,
        })
      );
    }
  };

  const onSubmit = (values, eventInfo) => {
    if (values.event !== "other-services") {
      onVideoCallSubmit(values, eventInfo);
    } else {
      onOtherServicesSubmit(values);
    }
  };

  return {
    onSubmit,
    onSubmitLoading: scheduleNonVideoEvent.isLoading || rescheduleNonVideoEvent.isLoading,
    onSubmitSuccess: scheduleNonVideoEvent.isSuccess || rescheduleNonVideoEvent.isSuccess,
    onSubmitError: scheduleNonVideoEvent.isError || rescheduleNonVideoEvent.isError,
    onCancelSubmit,
    onCancelSubmitLoading: cancelNonVideoEvent.isLoading || cancelVideoCallLoading,
    onCancelSubmitSuccess: cancelNonVideoEvent.isSuccess || cancelVideoCallSuccess,
    onCancelSubmitError: cancelNonVideoEvent.isError || cancelVideoCallFail,
  };
}
